import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Box, Button, Card, Alert, Chip, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, IconButton, Tooltip,
  Skeleton, Stack, Typography, Snackbar, Autocomplete, CircularProgress,
  InputAdornment, FormControl, InputLabel, Select, MenuItem, Checkbox,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Link,
} from '@mui/material';
import { DataGridPremium, GridToolbar, useGridApiRef, type GridColDef, type GridRenderCellParams } from '@mui/x-data-grid-premium';
import {
  Refresh as RefreshIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Download as DownloadIcon,
  MenuBook as MenuBookIcon,
  CloudUpload as CloudUploadIcon,
  InsertDriveFile as FileIcon,
  Close as CloseIcon,
  LocalOffer as TagIcon,
  History as HistoryIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';

import dayjs from 'dayjs';
import { PageHeader } from '@/common-components/layout';
import { useKBArticles, useKBTags, useUrlPagination } from '@/common-hooks';
import { useAuthentication } from '@/common-hooks/use-authentication';
import { kbArticleService } from '@/services/api';
import type { KBArticle, KBArticleTag, KBTagDefinition, KBArticleVersion } from '@/common-types';
import { FetchError } from '@/common-utilities/fetch-utilities';
import { PERMISSIONS } from '@/common-constants/permissions';
import { KbHistoryPanel } from './kb-history-panel';
import { KbTagManagerDialog } from './kb-tag-manager-dialog';

function downloadUrlAsFile(url: string, fileName: string): Promise<void> {
  return fetch(url)
    .then((r) => (r.ok ? r.blob() : Promise.reject(new Error(`Download failed (${r.status})`))))
    .then((blob) => {
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(a.href);
    })
    .catch((err) => {
      window.open(url, '_blank', 'noopener,noreferrer');
      return Promise.reject(err);
    });
}

/** New KB uploads in this UI: Markdown and plain text only. */
const KB_UPLOAD_ACCEPT = '.md,.txt';
const KB_UPLOAD_LABEL = 'MD, TXT';

const KB_ARTICLE_TEMPLATE_URL = '/kb-article-template.md';
const KB_ARTICLE_TEMPLATE_FILENAME = 'kb-article-template.md';

function fileTypeFromExtension(fileName: string): string {
  return fileName.split('.').pop()?.toUpperCase() || '';
}

function isKbUploadFileType(fileName: string): boolean {
  const t = fileTypeFromExtension(fileName);
  return t === 'MD' || t === 'TXT';
}

function chipColorForKbFileType(fileType: string): 'secondary' | 'default' {
  return fileType === 'MD' ? 'secondary' : 'default';
}

function getPutContentTypeForReplace(article: KBArticle): string {
  if (article.contentType) return article.contentType;
  if (article.fileType === 'MD') return 'text/markdown';
  if (article.fileType === 'TXT') return 'text/plain';
  return 'application/octet-stream';
}

function formatFileSize(bytes?: number): string {
  if (!bytes) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatTimestamp(ts?: string): string {
  if (!ts) return '—';
  const d = dayjs(ts);
  return d.isValid() ? d.format('MMM D, YYYY, h:mm A') : ts;
}

function KbArticleRowTooltip({ article }: { article: KBArticle }) {
  return (
    <Box sx={{ maxWidth: 440, maxHeight: 320, overflow: 'auto', py: 0.25 }}>
      <Typography variant="subtitle2" fontWeight={700} sx={{ lineHeight: 1.35 }}>
        {article.title}
      </Typography>
      {article.description ? (
        <Typography
          variant="body2"
          sx={{ mt: 1, opacity: 0.92, whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.45 }}
        >
          {article.description}
        </Typography>
      ) : (
        <Typography variant="caption" sx={{ mt: 1, display: 'block', opacity: 0.75 }}>
          No description
        </Typography>
      )}
      <Typography
        variant="caption"
        component="div"
        sx={{
          mt: 1.25,
          pt: 1,
          borderTop: '1px solid',
          borderColor: 'divider',
          fontFamily: 'monospace',
          wordBreak: 'break-all',
          lineHeight: 1.4,
        }}
      >
        {article.fileName}
      </Typography>
    </Box>
  );
}

function tagKeyOptionLabel(defs: KBTagDefinition[], key: string): string {
  const d = defs.find((t) => t.key === key);
  return d?.description ? `${key} — ${d.description}` : key;
}

function KbDocumentPreviewMarkdown({ source }: { source: string }) {
  return (
    <Box
      sx={{
        color: 'text.primary',
        fontSize: '0.875rem',
        lineHeight: 1.6,
        '& pre': {
          overflow: 'auto',
          maxWidth: '100%',
          bgcolor: 'grey.100',
          p: 1.5,
          borderRadius: 1,
          mb: 1.5,
        },
        '& p': { mb: 1.25, '&:last-child': { mb: 0 } },
        '& ul, & ol': { pl: 2.5, mb: 1.25 },
        '& table': {
          borderCollapse: 'collapse',
          width: '100%',
          mb: 1.5,
          '& th, & td': { border: '1px solid', borderColor: 'divider', p: 0.75 },
        },
        '& h1, & h2, & h3': { mt: 1.5, mb: 1, fontWeight: 600 },
      }}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: ({ href, children }) => (
            <Link href={href} target="_blank" rel="noopener noreferrer">
              {children}
            </Link>
          ),
        }}
      >
        {source}
      </ReactMarkdown>
    </Box>
  );
}

/** Distinct tag values per key across articles (sorted). */
function sortedValuesForArticleTagKey(availableTagValues: Map<string, Set<string>>, key: string): string[] {
  const acc = availableTagValues.get(key);
  if (!acc?.size) return [];
  return [...acc].sort((a, b) => a.localeCompare(b));
}

export default function AIKB() {
  const { hasPermission, isAdmin } = useAuthentication();
  const canAccess = hasPermission(PERMISSIONS.R_KB_IN_LOG_ORACLE);
  const canCreate = hasPermission(PERMISSIONS.C_KB_IN_LOG_ORACLE);
  const canUpdate = hasPermission(PERMISSIONS.U_KB_IN_LOG_ORACLE);
  const canDelete = hasPermission(PERMISSIONS.D_KB_IN_LOG_ORACLE);
  const { articles, loading, error, refresh, createArticle, updateArticle, deleteArticle } = useKBArticles();
  const {
    tagDefinitions,
    loading: tagsLoading,
    error: tagsError,
    refresh: refreshTagDefinitions,
  } = useKBTags(canAccess);

  const { page, rowsPerPage, setPage, setRowsPerPage } = useUrlPagination('kb');

  // Create modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newArticle, setNewArticle] = useState({ title: '', description: '' });
  const [newTags, setNewTags] = useState<KBArticleTag[]>([]);
  const [newTagKey, setNewTagKey] = useState('');
  const [newTagValue, setNewTagValue] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [creating, setCreating] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  /** Top filter bar (client-side; grid column filters still apply on top of this set). */
  const [search, setSearch] = useState('');
  const [filterFileType, setFilterFileType] = useState('');
  const [tagFilterKey, setTagFilterKey] = useState('');
  const [tagFilterValues, setTagFilterValues] = useState<string[]>([]);
  const filtersSignatureRef = useRef<string | null>(null);

  // -- Data loading --
  const fetchingRef = useRef(false);
  useEffect(() => {
    if (canAccess && !fetchingRef.current) {
      fetchingRef.current = true;
      refresh().finally(() => { fetchingRef.current = false; });
    }
  }, [canAccess, refresh]);
  const [selectedArticle, setSelectedArticle] = useState<KBArticle | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editTags, setEditTags] = useState<KBArticleTag[]>([]);
  const [editTagKey, setEditTagKey] = useState('');
  const [editTagValue, setEditTagValue] = useState('');
  const [updating, setUpdating] = useState(false);
  const [editReplaceFile, setEditReplaceFile] = useState<File | null>(null);
  const editReplaceFileInputRef = useRef<HTMLInputElement>(null);

  // Delete state
  const [deleting, setDeleting] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<KBArticle | null>(null);

  // Download state
  const [downloading, setDownloading] = useState<string | null>(null);

  const [previewArticle, setPreviewArticle] = useState<KBArticle | null>(null);
  const [previewBody, setPreviewBody] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  // Version history state
  const [versionHistoryArticle, setVersionHistoryArticle] = useState<KBArticle | null>(null);
  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false);
  const [versions, setVersions] = useState<KBArticleVersion[]>([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [versionsError, setVersionsError] = useState<string | null>(null);
  const [downloadingVersion, setDownloadingVersion] = useState<string | null>(null);

  const [manageTagsOpen, setManageTagsOpen] = useState(false);

  // Error / toast
  const [localError, setLocalError] = useState<string | null>(null);
  const [createModalError, setCreateModalError] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastSeverity, setToastSeverity] = useState<'success' | 'error'>('error');

  const articlesNoPdf = useMemo(
    () => articles.filter((a) => String(a.fileType ?? '').toUpperCase() !== 'PDF'),
    [articles],
  );

  /** Distinct types in loaded rows (for Type column filters). */
  const fileTypeFilterOptions = useMemo(() => {
    const types = new Set<string>(['MD', 'TXT']);
    for (const a of articlesNoPdf) {
      if (a.fileType) types.add(a.fileType);
    }
    return [...types].sort((a, b) => a.localeCompare(b));
  }, [articlesNoPdf]);

  const registeredTagKeys = useMemo(
    () => [...new Set(tagDefinitions.map((t) => t.key))].sort((a, b) => a.localeCompare(b)),
    [tagDefinitions],
  );

  /** Tag key options when adding/editing articles: registry keys plus keys already on articles. */
  const kbArticleTagKeyOptions = useMemo(() => {
    const s = new Set(registeredTagKeys);
    for (const article of articlesNoPdf) {
      for (const tag of article.tags || []) s.add(tag.key);
    }
    return [...s].sort((a, b) => a.localeCompare(b));
  }, [registeredTagKeys, articlesNoPdf]);

  const availableTagValues = useMemo(() => {
    const m = new Map<string, Set<string>>();
    for (const article of articlesNoPdf) {
      for (const tag of article.tags || []) {
        if (!m.has(tag.key)) m.set(tag.key, new Set());
        m.get(tag.key)!.add(tag.value);
      }
    }
    return m;
  }, [articlesNoPdf]);

  const filteredArticles = useMemo(() => {
    const q = search.trim().toLowerCase();
    return articlesNoPdf.filter((a) => {
      if (filterFileType && a.fileType !== filterFileType) return false;
      if (q) {
        const inTitle = (a.title || '').toLowerCase().includes(q);
        const inDesc = (a.description || '').toLowerCase().includes(q);
        const inFile = (a.fileName || '').toLowerCase().includes(q);
        const inTags = (a.tags || []).some(
          (t) =>
            `${t.key}: ${t.value}`.toLowerCase().includes(q)
            || (t.key || '').toLowerCase().includes(q)
            || (t.value || '').toLowerCase().includes(q),
        );
        if (!inTitle && !inDesc && !inFile && !inTags) return false;
      }
      if (tagFilterKey && tagFilterValues.length > 0) {
        const tags = a.tags || [];
        const match = tagFilterValues.some((v) => tags.some((t) => t.key === tagFilterKey && t.value === v));
        if (!match) return false;
      }
      return true;
    });
  }, [articlesNoPdf, search, filterFileType, tagFilterKey, tagFilterValues]);

  const hasActiveFilters =
    !!search.trim()
    || !!filterFileType
    || (!!tagFilterKey && tagFilterValues.length > 0);

  const maxPageIndex = Math.max(0, Math.ceil(filteredArticles.length / rowsPerPage) - 1);
  const effectivePage = Math.min(page, maxPageIndex);

  useEffect(() => {
    if (page > maxPageIndex) {
      setPage(maxPageIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setPage identity can churn; page/maxPageIndex are sufficient
  }, [page, maxPageIndex]);

  useEffect(() => {
    const sig = JSON.stringify({ search, filterFileType, tagFilterKey, tagFilterValues });
    if (filtersSignatureRef.current === null) {
      filtersSignatureRef.current = sig;
      return;
    }
    if (filtersSignatureRef.current === sig) return;
    filtersSignatureRef.current = sig;
    setPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only reset page when filter inputs change
  }, [search, filterFileType, tagFilterKey, tagFilterValues]);

  // -- Handlers --

  const handleCreateClick = () => {
    setNewArticle({ title: '', description: '' });
    setNewTags([]);
    setNewTagKey('');
    setNewTagValue('');
    setSelectedFile(null);
    setCreateModalError(null);
    setShowCreateModal(true);
  };

  const handleClearFilters = () => {
    setSearch('');
    setFilterFileType('');
    setTagFilterKey('');
    setTagFilterValues([]);
  };

  /** Resolve tag key to the registry spelling (case-insensitive). */
  const resolveRegisteredTagKey = (raw: string): string | null => {
    const k = raw.trim();
    if (!k) return null;
    return registeredTagKeys.find((rk) => rk.toLowerCase() === k.toLowerCase()) ?? null;
  };

  const unregisteredTagKeyMessage = (keyRaw: string) =>
    `Tag key "${keyRaw}" is not in the KB tag registry. ${
      isAdmin
        ? 'Register it under "Manage tag keys" or pick a registered key.'
        : 'Ask an administrator to register this key, or pick a registered key.'
    }`;

  /**
   * Tags that will be sent on create: chips in `newTags` plus key/value fields if filled.
   * Avoids losing tags when the user fills the row but forgets to click Add.
   */
  const buildCreatePayloadTags = (): KBArticleTag[] | null => {
    const merged: KBArticleTag[] = [...newTags];
    const keyRaw = newTagKey?.trim() || '';
    const valRaw = newTagValue?.trim() || '';
    if (!keyRaw && !valRaw) return merged;
    if (keyRaw && !valRaw) {
      setCreateModalError('Enter a tag value, or clear the tag key field, before uploading.');
      return null;
    }
    if (!keyRaw && valRaw) {
      setCreateModalError('Select a tag key for the value you entered, or clear the value field.');
      return null;
    }
    const canonicalKey = resolveRegisteredTagKey(keyRaw);
    if (!canonicalKey) {
      setCreateModalError(unregisteredTagKeyMessage(keyRaw));
      return null;
    }
    const next = { key: canonicalKey, value: valRaw };
    if (!merged.some((t) => t.key === next.key && t.value === next.value)) {
      merged.push(next);
    }
    return merged;
  };

  const handleAddNewTag = () => {
    const value = newTagValue?.trim() || '';
    const canonicalKey = resolveRegisteredTagKey(newTagKey || '');
    if (!canonicalKey || !value) return;
    const tagToAdd = { key: canonicalKey, value };
    if (newTags.some(t => t.key === tagToAdd.key && t.value === tagToAdd.value)) return;
    setNewTags([...newTags, tagToAdd]);
    setNewTagKey(canonicalKey);
    setNewTagValue('');
  };

  const handleRemoveNewTag = (index: number) => {
    setNewTags(newTags.filter((_, i) => i !== index));
  };

  const isBugIssueError =
    newTags.some(t => /^type$/i.test(t.key)) || (newTagKey ? /^type$/i.test(newTagKey) : false);
  const handleDownloadTemplate = () => {
    const url = `${window.location.origin}${KB_ARTICLE_TEMPLATE_URL}`;
    downloadUrlAsFile(url, KB_ARTICLE_TEMPLATE_FILENAME).catch(() => window.open(url, '_blank'));
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!isKbUploadFileType(file.name)) {
      setCreateModalError('Only Markdown (.md) and plain text (.txt) files can be uploaded.');
      setSelectedFile(null);
      return;
    }
    setCreateModalError(null);
    setSelectedFile(file);
  };

  const handleCreateArticle = async () => {
    if (!newArticle.title || !selectedFile) {
      setCreateModalError('Title and file are required');
      return;
    }
    if (!isKbUploadFileType(selectedFile.name)) {
      setCreateModalError('Only Markdown (.md) and plain text (.txt) files can be uploaded.');
      return;
    }
    const tagsForCreate = buildCreatePayloadTags();
    if (tagsForCreate === null) return;
    try {
      setCreating(true);
      setCreateModalError(null);
      const fileType = fileTypeFromExtension(selectedFile.name);
      await createArticle({
        title: newArticle.title,
        description: newArticle.description,
        fileType,
        tags: tagsForCreate,
      }, selectedFile);
      setCreateModalError(null);
      setShowCreateModal(false);
      setToastSeverity('success');
      setToastMessage('Article created successfully');
    } catch (err) {
      setCreateModalError(err instanceof Error ? err.message : 'Failed to create article');
    } finally {
      setCreating(false);
    }
  };

  const handleDownload = async (article: KBArticle) => {
    setDownloading(article.articleId);
    try {
      const { downloadUrl } = await kbArticleService.getArticleWithDownloadUrl(article.articleId);
      await downloadUrlAsFile(downloadUrl, article.fileName);
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : 'Download failed');
    } finally {
      setDownloading(null);
    }
  };

  const closeDocumentPreview = useCallback(() => {
    setPreviewArticle(null);
    setPreviewBody(null);
    setPreviewError(null);
  }, []);

  const handleOpenDocumentPreview = useCallback(async (article: KBArticle) => {
    const ft = String(article.fileType ?? '').toUpperCase();
    if (ft !== 'MD' && ft !== 'TXT') {
      setLocalError('Preview is only available for Markdown and plain text articles.');
      return;
    }
    setPreviewArticle(article);
    setPreviewBody(null);
    setPreviewError(null);
    setPreviewLoading(true);
    try {
      const { downloadUrl } = await kbArticleService.getArticleWithDownloadUrl(article.articleId);
      const res = await fetch(downloadUrl);
      if (!res.ok) throw new Error(`Could not load document (${res.status})`);
      setPreviewBody(await res.text());
    } catch (e) {
      setPreviewError(e instanceof Error ? e.message : 'Failed to load document');
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  const handleEditClick = (article: KBArticle) => {
    setSelectedArticle(article);
    setEditTitle(article.title);
    setEditDescription(article.description ?? '');
    setEditTags([...(article.tags || [])]);
    setEditTagKey('');
    setEditTagValue('');
    setEditReplaceFile(null);
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditReplaceFile(null);
  };

  const handleEditReplaceFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (selectedArticle && fileTypeFromExtension(file.name) !== selectedArticle.fileType) {
        setLocalError(
          `Choose a .${(selectedArticle.fileName.split('.').pop() || selectedArticle.fileType).toLowerCase()} file (same type as the current document: ${selectedArticle.fileType}).`
        );
        e.target.value = '';
        return;
      }
      setLocalError(null);
      setEditReplaceFile(file);
    }
    e.target.value = '';
  };

  const handleAddEditTag = () => {
    const value = editTagValue?.trim() || '';
    const canonicalKey = resolveRegisteredTagKey(editTagKey || '');
    if (!canonicalKey || !value) return;
    if (editTags.some(t => t.key === canonicalKey && t.value === value)) return;
    setEditTags([...editTags, { key: canonicalKey, value }]);
    setEditTagKey(canonicalKey);
    setEditTagValue('');
  };

  const handleRemoveEditTag = (index: number) => {
    setEditTags(editTags.filter((_, i) => i !== index));
  };

  const buildEditPayloadTags = (): KBArticleTag[] | null => {
    const merged: KBArticleTag[] = [...editTags];
    const keyRaw = editTagKey?.trim() || '';
    const valRaw = editTagValue?.trim() || '';
    if (!keyRaw && !valRaw) return merged;
    if (keyRaw && !valRaw) {
      setLocalError('Enter a tag value, or clear the tag key field, before saving.');
      return null;
    }
    if (!keyRaw && valRaw) {
      setLocalError('Select a tag key for the value you entered, or clear the value field.');
      return null;
    }
    const canonicalKey = resolveRegisteredTagKey(keyRaw);
    if (!canonicalKey) {
      setLocalError(unregisteredTagKeyMessage(keyRaw));
      return null;
    }
    const next = { key: canonicalKey, value: valRaw };
    if (!merged.some((t) => t.key === next.key && t.value === next.value)) {
      merged.push(next);
    }
    return merged;
  };

  const handleUpdateArticle = async () => {
    if (!selectedArticle || !editTitle) return;
    const tagsForSave = buildEditPayloadTags();
    if (tagsForSave === null) return;
    if (editReplaceFile) {
      const newType = fileTypeFromExtension(editReplaceFile.name);
      if (newType !== selectedArticle.fileType) {
        setLocalError(`Replacement file must be the same type as the current document (${selectedArticle.fileType}).`);
        return;
      }
    }
    try {
      setUpdating(true);
      setLocalError(null);
      let articleForPut = selectedArticle;
      if (editReplaceFile && !selectedArticle.contentType) {
        const full = await kbArticleService.getArticle(selectedArticle.articleId);
        articleForPut = { ...selectedArticle, contentType: full.contentType, fileType: full.fileType };
      }
      const putContentType = getPutContentTypeForReplace(articleForPut);
      await updateArticle(
        selectedArticle.articleId,
        {
          title: editTitle,
          description: editDescription,
          tags: tagsForSave,
        },
        editReplaceFile
          ? { replaceFile: editReplaceFile, putContentType }
          : undefined
      );
      closeEditModal();
      setToastSeverity('success');
      setToastMessage(
        editReplaceFile
          ? 'Article updated; new file uploaded. Ingestion runs daily at 3:00 AM UTC.'
          : 'Article updated successfully'
      );
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : 'Failed to update article');
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteArticle = async () => {
    if (!deleteConfirm) return;
    try {
      setDeleting(deleteConfirm.articleId);
      await deleteArticle(deleteConfirm.articleId);
      setDeleteConfirm(null);
      setToastSeverity('success');
      setToastMessage('Article deleted successfully');
    } catch (err) {
      if (err instanceof FetchError && err.status === 400) {
        setToastSeverity('error');
        setToastMessage(err.message || 'Failed to delete article');
        setDeleteConfirm(null);
      } else {
        setLocalError(err instanceof Error ? err.message : 'Failed to delete article');
      }
    } finally {
      setDeleting(null);
    }
  };

  const handleOpenVersionHistory = async (article: KBArticle) => {
    setVersionHistoryArticle(article);
    setVersions([]);
    setVersionsError(null);
    setVersionHistoryOpen(true);
    setVersionsLoading(true);
    try {
      const { article: articleFromApi, versions } = await kbArticleService.listArticleVersions(article.articleId);
      setVersions(versions);
      setVersionHistoryArticle(articleFromApi);
    } catch (err) {
      setVersionsError(err instanceof Error ? err.message : 'Failed to load version history');
    } finally {
      setVersionsLoading(false);
    }
  };

  const handleDownloadVersion = async (article: KBArticle, version: KBArticleVersion) => {
    if (!version.versionId) return;
    setDownloadingVersion(version.versionId);
    try {
      const { downloadUrl } = await kbArticleService.getArticleVersionDownloadUrl(article.articleId, version.versionId);
      await downloadUrlAsFile(downloadUrl, article.fileName);
    } catch (err) {
      setVersionsError(err instanceof Error ? err.message : 'Failed to download version');
    } finally {
      setDownloadingVersion(null);
    }
  };

  const kbGridApiRef = useGridApiRef();

  const kbArticlesGridColumns: GridColDef<KBArticle>[] = useMemo(
    () => [
      {
        field: 'title',
        headerName: 'Title',
        flex: 1.35,
        minWidth: 200,
        renderCell: (params: GridRenderCellParams<KBArticle, string>) => (
          <Tooltip
            title={<KbArticleRowTooltip article={params.row} />}
            placement="top-start"
            enterDelay={350}
            slotProps={{
              tooltip: { sx: { maxWidth: 480, p: 1.25 } },
            }}
          >
            <Box sx={{ cursor: 'default', minWidth: 0, width: '100%' }}>
              <Typography variant="body2" fontWeight={500} sx={{ display: 'block' }}>
                {params.row.title}
              </Typography>
              {params.row.description && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                  {params.row.description}
                </Typography>
              )}
            </Box>
          </Tooltip>
        ),
      },
      {
        field: 'fileType',
        headerName: 'Type',
        width: 100,
        minWidth: 100,
        type: 'singleSelect',
        valueOptions: [...fileTypeFilterOptions],
        renderCell: (params: GridRenderCellParams<KBArticle, string>) => (
          <Chip
            label={params.row.fileType}
            size="small"
            color={chipColorForKbFileType(params.row.fileType)}
            variant="outlined"
            sx={{ fontWeight: 600, fontSize: '0.7rem' }}
          />
        ),
      },
      {
        field: 'tagsJoined',
        headerName: 'Tags',
        flex: 1,
        minWidth: 180,
        sortable: false,
        valueGetter: (_value, row) =>
          (row.tags || []).map((t) => `${t.key}: ${t.value}`).join(' | '),
        renderCell: (params: GridRenderCellParams<KBArticle, string>) => {
          const article = params.row;
          return (
            <Stack direction="row" gap={0.5} flexWrap="wrap" sx={{ minWidth: 0, width: '100%' }}>
              {(article.tags || []).length === 0 && (
                <Typography variant="caption" color="text.disabled">-</Typography>
              )}
              {(article.tags || []).map((tag, i) => (
                <Chip
                  key={i}
                  icon={<TagIcon sx={{ fontSize: '0.75rem !important' }} />}
                  label={`${tag.key}: ${tag.value}`}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.7rem', height: 22 }}
                />
              ))}
            </Stack>
          );
        },
      },
      {
        field: 'createdAt',
        headerName: 'Created',
        width: 168,
        minWidth: 168,
        type: 'dateTime',
        align: 'right',
        headerAlign: 'right',
        valueGetter: (_value, row) => {
          if (!row.createdAt) return null;
          const d = dayjs(row.createdAt);
          return d.isValid() ? d.toDate() : null;
        },
        renderCell: (params: GridRenderCellParams<KBArticle, string>) => (
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            {formatTimestamp(params.row.createdAt)}
          </Typography>
        ),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 200,
        minWidth: 200,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: 'right',
        headerAlign: 'right',
        renderCell: (params: GridRenderCellParams<KBArticle>) => {
          const article = params.row;
          return (
            <Stack
              direction="row"
              gap={0.25}
              justifyContent="flex-end"
              alignItems="center"
              flexWrap="nowrap"
              sx={{ width: '100%', height: '100%', minHeight: 0 }}
            >
              {(article.fileType === 'MD' || article.fileType === 'TXT') && (
                <Tooltip title={previewLoading && previewArticle?.articleId === article.articleId ? 'Loading preview…' : 'View document'}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDocumentPreview(article)}
                      disabled={previewLoading && previewArticle?.articleId === article.articleId}
                      aria-label="View document"
                    >
                      {previewLoading && previewArticle?.articleId === article.articleId ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <VisibilityIcon fontSize="small" />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              <Tooltip title={downloading === article.articleId ? 'Downloading…' : 'Download'}>
                <IconButton size="small" onClick={() => handleDownload(article)} disabled={!!downloading} aria-label="Download article">
                  {downloading === article.articleId ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <DownloadIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
              <Tooltip title="Version history">
                <IconButton size="small" onClick={() => handleOpenVersionHistory(article)} aria-label="Version history">
                  <HistoryIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {canUpdate && (
                <Tooltip title="Edit">
                  <IconButton size="small" onClick={() => handleEditClick(article)} aria-label="Edit article">
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {canDelete && (
                <Tooltip title="Delete">
                  <IconButton size="small" color="error" onClick={() => setDeleteConfirm(article)} disabled={deleting === article.articleId} aria-label="Delete article">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          );
        },
      },
    ],
    [
      fileTypeFilterOptions,
      downloading,
      deleting,
      previewLoading,
      previewArticle,
      handleDownload,
      handleOpenDocumentPreview,
      handleEditClick,
      handleOpenVersionHistory,
      canUpdate,
      canDelete,
    ],
  );

  const KbArticlesNoRowsOverlay = () => (
    <Stack alignItems="center" justifyContent="center" sx={{ py: 6, height: '100%', gap: 1 }}>
      <MenuBookIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
      <Typography variant="body2" color="text.secondary" textAlign="center" px={2}>
        {hasActiveFilters
          ? 'No articles match the current filters.'
          : 'No articles in the knowledge base'}
      </Typography>
      {!hasActiveFilters && canCreate && (
        <Button size="small" startIcon={<AddIcon />} onClick={handleCreateClick}>
          Add Article
        </Button>
      )}
      {hasActiveFilters && (
        <Button size="small" onClick={handleClearFilters}>
          Clear filters
        </Button>
      )}
    </Stack>
  );

  if (!canAccess) {
    return <Alert severity="warning">You need Log Oracle permission to access this page.</Alert>;
  }

  return (
    <Box>
      <PageHeader
        title="AI Knowledge Base"
        subtitle={
          `${filteredArticles.length} article${filteredArticles.length !== 1 ? 's' : ''}`
          + (hasActiveFilters ? ` (${articlesNoPdf.length} total)` : '')
        }
        actions={
          <>
            {canCreate && (
              <Button variant="contained" size="small" startIcon={<AddIcon fontSize="small" />} onClick={handleCreateClick}>
                Add Article
              </Button>
            )}
            {isAdmin && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<TagIcon fontSize="small" />}
                onClick={() => setManageTagsOpen(true)}
              >
                Manage tag keys
              </Button>
            )}
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon fontSize="small" />}
              onClick={() => {
                void refresh();
                void refreshTagDefinitions();
              }}
              disabled={loading || tagsLoading}
            >
              Refresh
            </Button>
          </>
        }
      />

      <Alert severity="info" sx={{ mb: 2 }}>
        Knowledge base data is ingested daily at <strong>3:00 AM UTC</strong>. The AI chat will not know about newly uploaded or updated documents until the next ingestion run.
      </Alert>

      {(error || localError || tagsError) && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => {
            setLocalError(null);
          }}
        >
          {error || localError || tagsError}
        </Alert>
      )}

      {/* Filter bar — search, one file type, one tag key, multi-select tag values (OR among values) */}
      <Card sx={{ mb: 2, p: 2 }}>
        <Stack direction="row" alignItems="center" gap={1} mb={1.5}>
          <FilterListIcon fontSize="small" color="action" />
          <Typography variant="subtitle2" color="text.secondary">Filters</Typography>
          {hasActiveFilters && (
            <Button size="small" onClick={handleClearFilters} sx={{ ml: 'auto' }}>
              Clear all
            </Button>
          )}
        </Stack>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.25 }}>
          Optional tag filter: choose one key, then pick any number of values — an article matches if it has that key with any selected value (OR).
        </Typography>
        <Stack
          direction="row"
          flexWrap="wrap"
          alignItems="flex-end"
          useFlexGap
          gap={1.5}
          sx={{
            width: '100%',
            columnGap: 1.5,
            rowGap: 1.25,
            '& .MuiAutocomplete-root': { minWidth: 0 },
          }}
        >
          <TextField
            size="small"
            placeholder="Search articles..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              flex: { xs: '1 1 100%', sm: '1 1 200px' },
              width: { xs: '100%', sm: 'auto' },
              minWidth: { sm: 180 },
              maxWidth: { sm: 280 },
            }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              },
            }}
          />
          <FormControl
            size="small"
            sx={{
              flex: { xs: '1 1 100%', sm: '0 0 auto' },
              width: { xs: '100%', sm: 'auto' },
              minWidth: { sm: 152 },
            }}
          >
            <InputLabel>File type</InputLabel>
            <Select
              value={filterFileType}
              label="File type"
              onChange={(e) => setFilterFileType(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {fileTypeFilterOptions.map((ft) => (
                <MenuItem key={ft} value={ft}>{ft}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Autocomplete
            size="small"
            options={kbArticleTagKeyOptions}
            value={tagFilterKey || null}
            onChange={(_, val) => {
              setTagFilterKey(val || '');
              setTagFilterValues([]);
            }}
            getOptionLabel={(k) => tagKeyOptionLabel(tagDefinitions, k)}
            renderOption={(props, option) => (
              <li {...props}>
                <Box
                  component="span"
                  sx={{
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    lineHeight: 1.35,
                  }}
                >
                  {tagKeyOptionLabel(tagDefinitions, option)}
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Tag key"
                slotProps={{
                  htmlInput: {
                    ...params.inputProps,
                    title: tagFilterKey ? tagKeyOptionLabel(tagDefinitions, tagFilterKey) : undefined,
                  },
                }}
              />
            )}
            sx={{
              flex: { xs: '1 1 100%', sm: '0 1 180px' },
              width: { xs: '100%', sm: 'auto' },
              minWidth: { sm: 140 },
              maxWidth: { sm: 240 },
            }}
            slotProps={{
              paper: {
                sx: {
                  width: 'max-content',
                  maxWidth: '90vw',
                },
              },
              listbox: {
                sx: {
                  '& .MuiAutocomplete-option': {
                    alignItems: 'flex-start',
                    py: 1,
                  },
                },
              },
            }}
          />
          {!!tagFilterKey && (
            <Autocomplete
              multiple
              disableCloseOnSelect
              size="small"
              options={sortedValuesForArticleTagKey(availableTagValues, tagFilterKey)}
              value={tagFilterValues}
              onChange={(_, val) => setTagFilterValues(val)}
              getOptionLabel={(v) => v}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox
                    icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                    checkedIcon={<CheckBoxIcon fontSize="small" />}
                    sx={{ mr: 1, mt: 0.25, flexShrink: 0 }}
                    checked={selected}
                  />
                  <Box
                    component="span"
                    sx={{
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      lineHeight: 1.35,
                    }}
                  >
                    {option}
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Tag values"
                  placeholder="Select one or more"
                />
              )}
              sx={{
                flex: { xs: '1 1 100%', sm: '0 1 260px' },
                width: { xs: '100%', sm: 'auto' },
                minWidth: { sm: 180 },
                maxWidth: { sm: 320 },
              }}
              slotProps={{
                paper: {
                  sx: {
                    width: 'max-content',
                    maxWidth: 'min(90vw, 420px)',
                  },
                },
                listbox: {
                  sx: {
                    '& .MuiAutocomplete-option': {
                      alignItems: 'flex-start',
                      py: 1,
                    },
                  },
                },
              }}
            />
          )}
        </Stack>
      </Card>

      {/* Articles grid — toolbar Filters / column ⋮ filters apply on top of the filter bar */}
      <Card sx={{ overflow: 'hidden' }}>
        <Box sx={{ width: '100%', height: 560 }}>
          <DataGridPremium<KBArticle>
            apiRef={kbGridApiRef}
            rows={filteredArticles}
            columns={kbArticlesGridColumns}
            getRowId={(row) => row.articleId}
            loading={loading}
            pagination
            paginationMode="client"
            filterMode="client"
            paginationModel={{ page: effectivePage, pageSize: rowsPerPage }}
            onPaginationModelChange={(model) => {
              if (model.pageSize !== rowsPerPage) {
                setRowsPerPage(model.pageSize);
              }
              if (model.page !== page) {
                setPage(model.page);
              }
            }}
            onFilterModelChange={() => setPage(0)}
            pageSizeOptions={[5, 10, 25]}
            showToolbar
            disableDensitySelector
            disableRowSelectionOnClick
            rowHeight={68}
            columnHeaderHeight={40}
            slots={{
              toolbar: GridToolbar,
              noRowsOverlay: KbArticlesNoRowsOverlay,
            }}
            slotProps={{
              toolbar: {
                showQuickFilter: false,
                csvOptions: { disableToolbarButton: true },
                printOptions: { disableToolbarButton: true },
                excelOptions: { disableToolbarButton: true },
              },
            }}
            sx={{
              border: 'none',
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: 'grey.50',
                borderBottom: '2px solid',
                borderColor: 'divider',
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 600,
                fontSize: '0.75rem',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: 'text.secondary',
              },
              '& .MuiDataGrid-cell': {
                borderColor: 'divider',
                alignItems: 'center',
                py: 1,
              },
              '& .MuiDataGrid-cell .MuiDataGrid-cellContent': {
                width: '100%',
                height: '100%',
                minHeight: '100%',
                maxHeight: 'none',
                display: 'flex',
                alignItems: 'center',
              },
              '& .MuiDataGrid-cell[data-field="title"] .MuiDataGrid-cellContent, & .MuiDataGrid-cell[data-field="tagsJoined"] .MuiDataGrid-cellContent':
                {
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  whiteSpace: 'normal',
                  lineHeight: 1.35,
                },
              '& .MuiDataGrid-cell[data-field="createdAt"] .MuiDataGrid-cellContent, & .MuiDataGrid-cell[data-field="actions"] .MuiDataGrid-cellContent':
                {
                  justifyContent: 'flex-end',
                },
              '& .MuiDataGrid-footerContainer': {
                borderTop: '1px solid',
                borderColor: 'divider',
              },
              '& .MuiDataGrid-toolbarContainer': {
                gap: 1,
                flexWrap: 'wrap',
                borderBottom: '1px solid',
                borderColor: 'divider',
                px: 1.5,
                py: 1,
              },
            }}
          />
        </Box>
      </Card>

      {/* Create Article Dialog */}
      <Dialog
        open={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setCreateModalError(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            pr: 2,
            pb: 1,
          }}
        >
          Add Knowledge Base Article
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon fontSize="small" />}
            onClick={handleDownloadTemplate}
          >
            Download template
          </Button>
        </DialogTitle>
        <DialogContent>
          <Stack gap={2} mt={1}>
            {createModalError && (
              <Alert severity="error" onClose={() => setCreateModalError(null)}>
                {createModalError}
              </Alert>
            )}
            {/* Tags – one section at top: key + value + Add */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>Tags</Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Tag keys come from the KB tag registry. Only registered keys can be saved on an article.
                Filled key and value are included when you upload even if you do not click Add.
              </Typography>
              {!tagsLoading && registeredTagKeys.length === 0 && (
                <Alert severity="warning" sx={{ mb: 1 }}>
                  No tag keys are registered yet. Create tag definitions via the KB Tags API before tagging articles.
                </Alert>
              )}
              {newTags.length > 0 && (
                <Stack direction="row" gap={0.5} flexWrap="wrap" mb={1}>
                  {newTags.map((tag, i) => (
                    <Chip
                      key={i}
                      icon={<TagIcon sx={{ fontSize: '0.8rem !important' }} />}
                      label={`${tag.key}: ${tag.value}`}
                      size="small"
                      onDelete={() => handleRemoveNewTag(i)}
                      variant="outlined"
                    />
                  ))}
                </Stack>
              )}
              <Stack direction="row" gap={1} alignItems="flex-start">
                <Autocomplete
                  size="small"
                  options={kbArticleTagKeyOptions}
                  loading={tagsLoading}
                  value={newTagKey || null}
                  onChange={(_, val) => setNewTagKey(val || '')}
                  getOptionLabel={(k) => tagKeyOptionLabel(tagDefinitions, k)}
                  renderInput={(params) => (
                    <TextField {...params} label="Tag key" placeholder="Select a registered key" />
                  )}
                  sx={{ flex: 1 }}
                />
                <TextField
                  size="small"
                  label="Value"
                  value={newTagValue}
                  onChange={(e) => setNewTagValue(e.target.value)}
                  placeholder="e.g., DynamoDB"
                  sx={{ flex: 1 }}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddNewTag(); } }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleAddNewTag}
                  disabled={!newTagKey || !newTagValue || kbArticleTagKeyOptions.length === 0}
                  sx={{ minWidth: 'auto', px: 2, height: 40 }}
                >
                  Add
                </Button>
              </Stack>
            </Box>

            <TextField
              label="Title"
              value={newArticle.title}
              onChange={(e) => setNewArticle({ ...newArticle, title: e.target.value })}
              size="small"
              fullWidth
              required
              placeholder="e.g., DynamoDB throttling during BCP failover"
            />
            <TextField
              label="Description"
              value={newArticle.description}
              onChange={(e) => setNewArticle({ ...newArticle, description: e.target.value })}
              size="small"
              fullWidth
              multiline
              rows={3}
              placeholder="Brief summary of the issue and resolution..."
            />

            {isBugIssueError && (
              <Alert severity="info" sx={{ '& .MuiAlert-message': { width: '100%' } }}>
                <Typography variant="body2" component="div">
                  For bug/issue/error articles, follow the Markdown template structure (Error Code, Root Cause, Resolution). Use{' '}
                  <strong>Download template</strong> above for {KB_ARTICLE_TEMPLATE_FILENAME}.
                </Typography>
              </Alert>
            )}

            {/* File Upload */}
            <Box>
              <input
                ref={fileInputRef}
                type="file"
                hidden
                accept={KB_UPLOAD_ACCEPT}
                onChange={handleFileSelect}
              />
              <Button
                variant="outlined"
                startIcon={<CloudUploadIcon />}
                onClick={() => fileInputRef.current?.click()}
                fullWidth
                sx={{ justifyContent: 'flex-start', textTransform: 'none', py: 1.5 }}
              >
                {selectedFile ? selectedFile.name : 'Choose file to upload...'}
              </Button>
              {selectedFile && (
                <Stack direction="row" alignItems="center" gap={1} mt={0.5}>
                  <FileIcon fontSize="small" color="action" />
                  <Typography variant="caption" color="text.secondary">
                    {fileTypeFromExtension(selectedFile.name)} - {formatFileSize(selectedFile.size)}
                  </Typography>
                  <IconButton size="small" onClick={() => setSelectedFile(null)}>
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Stack>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Supported: {KB_UPLOAD_LABEL}
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowCreateModal(false);
              setCreateModalError(null);
            }}
            disabled={creating}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCreateArticle} disabled={creating || !newArticle.title || !selectedFile}>
            {creating ? <Skeleton variant="text" width={80} height={20} /> : 'Add Article'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Article Dialog */}
      <Dialog open={showEditModal} onClose={closeEditModal} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Article</DialogTitle>
        <DialogContent>
          <Stack gap={2} mt={1}>
            <TextField label="Article ID" value={selectedArticle?.articleId || ''} disabled size="small" fullWidth />
            <TextField
              label="Title"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              size="small"
              fullWidth
              required
            />
            <TextField
              label="Description"
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              size="small"
              fullWidth
              multiline
              rows={3}
            />

            {selectedArticle && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>Document</Typography>
                <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap" sx={{ mb: 0.5 }}>
                  <FileIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary" fontFamily="monospace" fontSize="0.8rem">
                    {selectedArticle.fileName}
                  </Typography>
                  <Chip label={selectedArticle.fileType} size="small" color={chipColorForKbFileType(selectedArticle.fileType)} variant="outlined" sx={{ fontSize: '0.7rem' }} />
                </Stack>
                <input
                  ref={editReplaceFileInputRef}
                  type="file"
                  hidden
                  accept="*"
                  onChange={handleEditReplaceFileSelect}
                />
                <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" sx={{ mt: 0.5 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<CloudUploadIcon fontSize="small" />}
                    onClick={() => editReplaceFileInputRef.current?.click()}
                    disabled={updating}
                  >
                    Replace file
                  </Button>
                  {editReplaceFile && (
                    <Stack direction="row" alignItems="center" gap={0.5}>
                      <Typography variant="body2" color="text.primary" noWrap sx={{ maxWidth: 200 }}>
                        {editReplaceFile.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatFileSize(editReplaceFile.size)}
                      </Typography>
                      <IconButton size="small" aria-label="Clear replacement file" onClick={() => setEditReplaceFile(null)}>
                        <CloseIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Stack>
                  )}
                </Stack>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
                  The new file must be the same type as the current document. It overwrites the existing file in storage (older versions are kept if S3 versioning is enabled on the bucket).
                </Typography>
              </Box>
            )}

            {/* Tags */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>Tags</Typography>
              {editTags.length > 0 && (
                <Stack direction="row" gap={0.5} flexWrap="wrap" mb={1}>
                  {editTags.map((tag, i) => (
                    <Chip
                      key={i}
                      icon={<TagIcon sx={{ fontSize: '0.8rem !important' }} />}
                      label={`${tag.key}: ${tag.value}`}
                      size="small"
                      onDelete={() => handleRemoveEditTag(i)}
                      variant="outlined"
                    />
                  ))}
                </Stack>
              )}
              <Stack direction="row" gap={1} alignItems="flex-start">
                <Autocomplete
                  size="small"
                  options={kbArticleTagKeyOptions}
                  loading={tagsLoading}
                  value={editTagKey || null}
                  onChange={(_, val) => setEditTagKey(val || '')}
                  getOptionLabel={(k) => tagKeyOptionLabel(tagDefinitions, k)}
                  renderInput={(params) => (
                    <TextField {...params} label="Tag key" placeholder="Select a registered key" />
                  )}
                  sx={{ flex: 1 }}
                />
                <TextField
                  size="small"
                  label="Tag Value"
                  value={editTagValue}
                  onChange={(e) => setEditTagValue(e.target.value)}
                  sx={{ flex: 1 }}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddEditTag(); } }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleAddEditTag}
                  disabled={!editTagKey || !editTagValue || kbArticleTagKeyOptions.length === 0}
                  sx={{ minWidth: 'auto', px: 2, height: 40 }}
                >
                  Add
                </Button>
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditModal} disabled={updating}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdateArticle} disabled={updating || !editTitle}>
            {updating ? <Skeleton variant="text" width={50} height={20} /> : (editReplaceFile ? 'Save & upload' : 'Save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Article?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Delete <strong>{deleteConfirm?.title}</strong>? This will remove the document from the knowledge base and cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)} disabled={!!deleting}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteArticle} disabled={!!deleting}>
            {deleting ? <Skeleton variant="text" width={60} height={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast */}
      <Snackbar
        open={!!toastMessage}
        autoHideDuration={4000}
        onClose={() => setToastMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setToastMessage(null)} severity={toastSeverity} sx={{ width: '100%' }}>
          {toastMessage}
        </Alert>
      </Snackbar>

      {/* MD / TXT document preview */}
      <Dialog
        open={!!previewArticle}
        onClose={closeDocumentPreview}
        maxWidth="md"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              height: 'min(85vh, 800px)',
              maxHeight: '85vh',
              display: 'flex',
              flexDirection: 'column',
            },
          },
        }}
      >
        <DialogTitle sx={{ pb: 1, flexShrink: 0 }}>
          <Stack direction="row" alignItems="flex-start" gap={1} flexWrap="wrap" justifyContent="space-between">
            <Box sx={{ minWidth: 0, flex: '1 1 auto' }}>
              <Typography variant="subtitle1" fontWeight={600} component="div">
                {previewArticle?.title ?? 'Document'}
              </Typography>
              <Typography variant="caption" color="text.secondary" fontFamily="monospace" display="block" sx={{ wordBreak: 'break-all' }}>
                {previewArticle?.fileName}
              </Typography>
            </Box>
            {previewArticle && (
              <Chip
                size="small"
                label={previewArticle.fileType}
                variant="outlined"
                color={chipColorForKbFileType(previewArticle.fileType)}
                sx={{ flexShrink: 0, fontWeight: 600 }}
              />
            )}
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          {previewLoading && (
            <Stack alignItems="center" justifyContent="center" py={6}>
              <CircularProgress />
            </Stack>
          )}
          {!previewLoading && previewError && (
            <Alert severity="error">{previewError}</Alert>
          )}
          {!previewLoading && !previewError && previewBody !== null && previewArticle && (
            previewArticle.fileType === 'MD' ? (
              <KbDocumentPreviewMarkdown source={previewBody} />
            ) : (
              <Typography
                component="pre"
                variant="body2"
                sx={{
                  m: 0,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  fontFamily: 'monospace',
                  fontSize: '0.8125rem',
                }}
              >
                {previewBody}
              </Typography>
            )
          )}
        </DialogContent>
        <DialogActions sx={{ flexShrink: 0 }}>
          <Button onClick={closeDocumentPreview}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Version History Dialog */}
      <Dialog
        open={versionHistoryOpen}
        onClose={() => { setVersionHistoryOpen(false); setVersionsError(null); }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" gap={1}>
            <HistoryIcon fontSize="small" color="action" />
            <Box>
              <Typography variant="subtitle1" fontWeight={600} component="span">
                Version History
              </Typography>
              {versionHistoryArticle && (
                <Typography variant="caption" color="text.secondary" display="block">
                  {versionHistoryArticle.title}
                </Typography>
              )}
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {versionsError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setVersionsError(null)}>
              {versionsError}
            </Alert>
          )}
          {versionsLoading ? (
            <Stack gap={1} mt={1}>
              {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} height={40} />)}
            </Stack>
          ) : (
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  File versions (S3)
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Older file bytes when the KB bucket has versioning enabled; use Replace File on an article to create a new S3 version.
                </Typography>
                {versions.length === 0 ? (
                  <Box sx={{ py: 3, textAlign: 'center', px: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                    <HistoryIcon sx={{ fontSize: 32, color: 'text.disabled', mb: 0.5 }} />
                    <Typography variant="body2" color="text.secondary">
                      No S3 file versions returned.
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      This is normal if versioning is off on the bucket, or the file was never replaced.
                    </Typography>
                  </Box>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Version date</TableCell>
                          <TableCell>Modified by</TableCell>
                          <TableCell>Size</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Version ID</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {versions.map((v, i) => (
                          <TableRow key={v.versionId ?? i} hover>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                              {formatTimestamp(v.lastModified ?? undefined)}
                            </TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                              {v.uploadedBy ? (
                                <Tooltip title={v.uploadedBy}>
                                  <Typography variant="body2" sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {v.uploadedBy}
                                  </Typography>
                                </Tooltip>
                              ) : (
                                <Typography variant="body2" color="text.disabled">—</Typography>
                              )}
                            </TableCell>
                            <TableCell>{formatFileSize(v.size ?? undefined)}</TableCell>
                            <TableCell>
                              {v.isLatest ? (
                                <Chip label="Latest" size="small" color="success" variant="outlined" sx={{ fontSize: '0.7rem', height: 20 }} />
                              ) : (
                                <Chip label={`v${versions.length - i}`} size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 20 }} />
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption" fontFamily="monospace" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                                {v.versionId ? `${v.versionId.substring(0, 20)}…` : 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title={v.versionId ? 'Download this version' : 'Versioning not enabled — download current file from the main table'}>
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={!v.versionId || downloadingVersion === v.versionId}
                                    onClick={() => versionHistoryArticle && handleDownloadVersion(versionHistoryArticle, v)}
                                    aria-label="Download version"
                                  >
                                    {downloadingVersion === v.versionId ? (
                                      <CircularProgress size={16} color="inherit" />
                                    ) : (
                                      <DownloadIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>

              <Box>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Change history 
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Creates, metadata updates, file replacements, and tag changes on this article.
                </Typography>
                <KbHistoryPanel
                  entries={versionHistoryArticle?.history}
                  emptyMessage="No DynamoDB history on this article yet (older rows may predate history tracking)."
                />
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setVersionHistoryOpen(false); setVersionsError(null); }}>Close</Button>
        </DialogActions>
      </Dialog>

      {isAdmin && (
        <KbTagManagerDialog
          open={manageTagsOpen}
          onClose={() => setManageTagsOpen(false)}
          tags={tagDefinitions}
          loading={tagsLoading}
          onTagsChanged={async () => {
            await refreshTagDefinitions();
            await refresh();
          }}
        />
      )}
    </Box>
  );
}
